import { Button, notification } from 'antd';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const NOTIFICATION_KEY = 'app-version-update';
const MAIN_SCRIPT_SELECTOR = 'script[src*="/static/js/main."]';

let timerId = null;
let runningHash = '';
let deployRoot = '';
let notified = false;

const hashOf = (url = '') => url.match(/\/static\/js\/main\.([^.]+)\.js/)?.[1] || '';

const reloadApp = async () => {
  try {
    if (window.caches) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map(key => window.caches.delete(key)));
    }
  } catch {
    // Cache API unavailable or blocked: reload anyway.
  }
  window.location.reload();
};

const stopWatching = () => {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
  document.removeEventListener('visibilitychange', onVisibilityChange);
};

const showUpdateNotification = () => {
  notified = true;
  stopWatching();

  notification.open({
    key: NOTIFICATION_KEY,
    message: <b>A new version of Clin-Ed is available.</b>,
    description: (
      <div className='text-slate-500 text-sm'>
        Reload the page to get the latest updates. Any unsaved changes will be lost.
      </div>
    ),
    btn: (
      <Button type='primary' size='small' onClick={reloadApp}>
        Reload
      </Button>
    ),
    duration: 0,
    closeIcon: false,
    placement: 'topRight',
    role: 'alert'
  });
};

const checkForUpdate = async () => {
  if (notified || document.visibilityState !== 'visible') return;

  try {
    // index.html is ~630 bytes and already names the deployed main.<hash>.js,
    // so it is a much cheaper source for the same signal than asset-manifest.json.
    const res = await fetch(`${deployRoot}index.html?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit'
    });
    if (!res.ok) return;
    const deployedHash = hashOf(await res.text());

    if (!deployedHash || deployedHash === runningHash) return;
    showUpdateNotification();
  } catch {
    // Offline, 404, or an unexpected body: stay quiet and retry on the next tick.
  }
};

function onVisibilityChange() {
  if (document.visibilityState === 'visible') checkForUpdate();
}

export const startVersionWatcher = () => {
  // In dev the entry script is bundle.js, so there is no hash to compare.
  if (process.env.NODE_ENV !== 'production') return;

  const mainScript = document.querySelector(MAIN_SCRIPT_SELECTOR);
  const src = mainScript?.src || '';
  runningHash = hashOf(src);
  if (!runningHash) return;

  deployRoot = src.slice(0, src.indexOf('static/js/'));

  checkForUpdate();

  timerId = setInterval(checkForUpdate, POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
};

export default startVersionWatcher;
